import { Check, Clock, FilePenLine } from 'lucide-react';
import { sortBy } from 'lodash';
import { PROFILE_STEPS, getFirstIncompleteStep, getStepById, stepRoute } from 'pages/Profile/helper';
import { pendingForms } from 'components/Profile/Consent/helper';
import { carrierTitle } from 'components/Profile/Insurance/helper';
import { PORTAL_ROUTES } from 'utils/constants';

export const CARD_BTN = {
  primary: 'border-none bg-secondary-900 text-white hover:bg-black',
  outline: 'border-secondary-200 bg-white text-secondary-900 hover:border-secondary-300 hover:bg-secondary-50',
};

const BADGES = {
  complete: { status: 'Complete', icon: Check, className: 'bg-success-50 text-green-700 border-green-200' },
  next: { status: 'Next up', icon: null, className: 'bg-blue-50 text-blue-700 border-blue-100' },
  notStarted: { status: 'Not started', icon: null, className: 'bg-secondary-100 text-secondary-600 border-secondary-200' },
};

export const MAX_ROWS = 4;

const STEP_MINUTES = { personal: 2, insurance: 1, medical: 2, hipaa: 1, consent: 1 };

const minutesLabel = (ids) => {
  const minutes = ids.reduce((total, id) => total + STEP_MINUTES[id], 0);
  return `About ${minutes} minute${minutes === 1 ? '' : 's'}`;
};

const incompleteIds = (completion) => PROFILE_STEPS.filter(({ id }) => !completion[id]).map(({ id }) => id);

export const getCompletedCount = (completion) => PROFILE_STEPS.length - incompleteIds(completion).length;

export const getMinutesLeft = (completion) =>
  incompleteIds(completion).reduce((total, id) => total + STEP_MINUTES[id], 0);

const formRows = (forms = []) =>
  sortBy(forms, ({ attributes }) => !!attributes?.status)
    .map(({ id, attributes }) => ({ id, label: attributes?.title, done: !!attributes?.status }));

// One entry per dashboard card that maps to profile steps. `steps` drives the icon, badge, route and time.
const STEP_CARDS = [
  {
    steps: ['personal', 'insurance', 'medical'],
    title: 'Your information',
    description: 'Personal details, insurance and medical history.',
    rows: ({ insurance }, completion) => {
      const carrier = insurance?.active ? carrierTitle(insurance) : '';

      return ['personal', 'insurance', 'medical'].map((id) => ({
        id,
        label: [getStepById(id).title, id === 'insurance' && carrier].filter(Boolean).join(' · '),
        done: !!completion[id],
      }));
    },
  },
  {
    steps: ['hipaa'],
    title: 'HIPAA & Privacy',
    description: 'Sign the privacy notice and, if you like, name people we can talk to about your care.',
    rows: ({ hipaa, contacts }) => [
      ...formRows(hipaa),
      { id: 'authorization', label: 'People who can discuss your care', done: contacts?.authorized?.length > 0, optional: true },
    ],
  },
  {
    steps: ['consent'],
    title: 'Office consents',
    description: 'The standard agreements every patient signs once.',
    rows: ({ consent }) => formRows(consent),
  },
];

export const getStepCards = (sectionData: any = {}, completion = {}) =>
  STEP_CARDS.map(({ steps, title, description, rows }) => {
    const first = getStepById(steps[0]);
    const pending = steps.filter((id) => !completion[id]);
    const isNext = pending.includes(getFirstIncompleteStep(completion).id);
    const badge = pending.length ? (isNext ? BADGES.next : BADGES.notStarted) : BADGES.complete;

    return {
      key: steps[0],
      icon: first.icon,
      iconClass: `${first.iconBg} ${first.iconColor}`,
      badge,
      highlight: isNext ? 'border-blue-300' : '',
      title,
      description,
      rows: rows(sectionData, completion),
      footer: pending.length ? minutesLabel(pending) : '',
      button: {
        to: stepRoute(getStepById(pending[0] || steps[0])),
        label: pending.length ? 'Start' : 'Review',
        primary: isNext,
      },
    };
  });

export const treatmentConsentsCard = ({ treatmentConsents = [] }) => {
  const pending = pendingForms(treatmentConsents).length;

  return {
    icon: FilePenLine,
    iconClass: 'bg-amber-100 text-amber-600',
    badge: pending
      ? { status: `${pending} to sign`, icon: Clock, className: 'bg-amber-50 text-amber-700 border-amber-200' }
      : BADGES.complete,
    highlight: pending ? 'border-amber-200' : '',
    title: 'Treatment consents',
    description: pending
      ? 'Consents for your planned treatment. Sign before your appointment.'
      : 'All consents for your planned treatment are signed.',
    rows: formRows(treatmentConsents).map((row) => ({ ...row, pendingClass: 'text-amber-500' })),
    button: { to: PORTAL_ROUTES.TREATMENT_CONSENTS, label: pending ? 'Review & sign' : 'View' },
  };
};
